/**
 * Turns the API's per-channel presence into something a human can act on.
 *
 * PRESENCE IS PER SURFACE, NOT PER ACCOUNT. The API returns
 * `appPresence: { vendorApp, driverApp, vendorWeb, adminWeb }` and these are deliberately
 * never merged. A restaurant with the dashboard open in the office and the
 * tablet switched off in the kitchen is a restaurant that will not see its next
 * order, and a single combined "online" dot would say the opposite.
 *
 * The API returns raw timestamps rather than a precomputed status, so the
 * thresholds below belong to the dashboard and changing them is an admin-only
 * deploy. Keep them in the same ballpark as APP_PRESENCE_FRESH_MS in
 * api/src/util/appPresence.js, which governs the counts the API derives itself.
 *
 * WHAT A STALE VALUE PROVES. For a channel still on the inferred signal
 * (`source: "poll"`), the app's polling loop is bound to a screen, so a stale
 * timestamp does NOT prove the app is closed — it may be backgrounded and still
 * able to receive a push and print. Only `source: "heartbeat"` is
 * screen-independent, and only a heartbeat reports `state`. Labels are worded
 * to describe what we last saw, not to assert what is true now.
 */
const APP_ONLINE_MS = 60_000;
const APP_IDLE_MS = 10 * 60_000;

export const PRESENCE_TONE = {
    ON: "on",
    IDLE: "idle",
    OFF: "off",
    /** Alive but not being watched — only knowable from a heartbeat. */
    BACKGROUND: "background",
    /** Drivers only: declared available, but the app has gone quiet. */
    WARN: "warn",
};

/** An empty channel, for rows the API has never seen on that surface. */
const EMPTY_CHANNEL = {
    lastSeenAt: null,
    lastForegroundAt: null,
    state: null,
    source: null,
    appVersion: null,
    platform: null,
    printerConnected: null,
    notificationsEnabled: null,
};

/**
 * @param {object} presence - the row's `appPresence` object.
 * @param {"vendorApp"|"driverApp"|"vendorWeb"|"adminWeb"} channel
 */
export function getChannel(presence, channel) {
    return presence?.[channel] ?? EMPTY_CHANNEL;
}

const minutesAgo = (ms) => Math.max(1, Math.round(ms / 60_000));

/**
 * State of one channel.
 *
 * @param {object} channel - one entry from `appPresence`.
 * @param {string} neverLabel - wording for "we have never seen this surface",
 *   which differs per channel ("لم يفتح التطبيق" vs "لم يدخل لوحة التحكم").
 */
export function getChannelState(channel, neverLabel) {
    const { lastSeenAt, state, source } = channel ?? EMPTY_CHANNEL;

    if (!lastSeenAt) return { tone: PRESENCE_TONE.OFF, label: neverLabel };

    const ageMs = Date.now() - new Date(lastSeenAt).getTime();
    if (Number.isNaN(ageMs)) return { tone: PRESENCE_TONE.OFF, label: "غير معروف" };

    if (ageMs < APP_ONLINE_MS) {
        // Only a heartbeat can distinguish "running but backgrounded" from
        // "on screen". Without one, a fresh poll already implies foreground.
        if (source === "heartbeat" && state === "background") {
            return { tone: PRESENCE_TONE.BACKGROUND, label: "شغّال بالخلفية" };
        }
        return { tone: PRESENCE_TONE.ON, label: "مفتوح" };
    }

    if (ageMs < APP_IDLE_MS) {
        return { tone: PRESENCE_TONE.IDLE, label: `منذ ${minutesAgo(ageMs)} د` };
    }

    return { tone: PRESENCE_TONE.OFF, label: "مغلق" };
}

/**
 * The driver app, cross-checked against the driver's own availability toggle.
 *
 * `users.online` is written by the driver app and cleared by nothing, so a
 * driver who taps "متصل" and closes the app stays flagged available forever.
 * That disagreement gets its own tone rather than being folded into a plain
 * "مغلق", because it is the state that silently costs orders.
 */
export function getDriverAppState(isOnline, presence) {
    const channel = getChannel(presence, "driverApp");
    const state = getChannelState(channel, "لم يفتح التطبيق");

    if (isOnline && state.tone === PRESENCE_TONE.OFF) {
        return { tone: PRESENCE_TONE.WARN, label: "متصل — التطبيق مغلق" };
    }

    return state;
}

/**
 * Capability faults worth surfacing next to a live restaurant app: a connected
 * app that still cannot deliver an order to a human.
 *
 * Tri-state matters here — `null` means the build does not report the field,
 * and an unknown printer must never be drawn as a broken one.
 */
export function getChannelFaults(channel) {
    const faults = [];
    if (channel?.printerConnected === false) faults.push("الطابعة غير موصولة");
    if (channel?.notificationsEnabled === false) faults.push("الإشعارات موقوفة");
    return faults;
}
